import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  CheckCircle, 
  ArrowLeft, 
  ArrowRight, 
  Sparkles,
  Brain,
  Zap,
  Eye,
  Wand2,
  Clock,
  Target
} from 'lucide-react';
import { ImportType } from './ImportTypeSelector';

interface FieldMappingProps {
  importType: ImportType;
  fileData: {
    headers: string[];
    sampleData: string[][];
    totalRows: number;
  };
  suggestedMappings: Record<string, { field: string; confidence: number }>;
  workerResults?: any; // Full Worker analysis results
  onMappingComplete: (mappings: Record<string, string>) => void;
  onBack: () => void;
}

interface AIInsight {
  sourceColumn: string;
  targetField: string;
  confidence: number;
  reasoning: string;
  transformation?: string;
}

// AI analysis steps that happen in the Cloudflare Worker
const aiAnalysisSteps = [
  {
    id: 'source-detection',
    title: 'Detecting Source System',
    description: 'Analyzing file structure and column patterns...'
  },
  {
    id: 'field-mapping',
    title: 'Smart Field Mapping', 
    description: 'AI is matching your columns to Hivetivity fields...'
  },
  {
    id: 'data-validation',
    title: 'Data Quality Check',
    description: 'Checking data formats and identifying issues...'
  },
  {
    id: 'transformation-planning',
    title: 'Planning Transformations',
    description: 'Preparing automatic data cleanup and formatting...'
  }
];

export function FieldMapping({ 
  importType, 
  fileData, 
  suggestedMappings, 
  workerResults: propsWorkerResults,
  onMappingComplete, 
  onBack 
}: FieldMappingProps) {
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [workerResults, setWorkerResults] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedMappings, setEditedMappings] = useState<Record<string, string>>({});

  // Available target fields based on actual students table schema
  const getAvailableFields = (importType: ImportType): string[] => {
    const fieldMap: Record<string, string[]> = {
      students: [
        'first_name', 'last_name', 'preferred_name', 'date_of_birth', 'gender', 
        'email', 'phone', 'address_line1', 'address_line2', 'city', 'state', 'zip',
        'notes', 'start_date', 'drop_date', 'no_public_photos', 'uses_parent_address'
      ],
      families: ['name', 'billing_status', 'billing_preference', 'is_active'],
      classes: ['class_name'], // TODO: Add actual class fields  
      enrollments: ['student_name', 'class_name'], // TODO: Add actual enrollment fields
      payments: ['amount'], // TODO: Add actual payment fields
      staff: ['first_name', 'last_name', 'email'] // TODO: Add actual staff fields
    };
    
    return ['-- Skip this column --', ...(fieldMap[importType] || [])];
  };

  // Initialize edited mappings when worker results change
  useEffect(() => {
    // Include all headers, mapped or unmapped
    const initialMappings = fileData.headers.reduce((acc, header) => {
      const mapping = workerResults?.mappings?.[header];
      acc[header] = mapping?.targetField || '';
      return acc;
    }, {} as Record<string, string>);
    setEditedMappings(initialMappings);
  }, [workerResults, fileData.headers]);

  // Call the actual Cloudflare Worker for AI analysis
  useEffect(() => {
    const analyzeWithWorker = async () => {
      try {
        // Create FormData for the Worker API
        const formData = new FormData();
        // Note: In real implementation, we'd get the actual file from previous step
        // For now, simulate the analysis call
        
        // Step through AI analysis UI
        for (let i = 0; i < aiAnalysisSteps.length; i++) {
          setCurrentStep(i);
          await new Promise(resolve => setTimeout(resolve, 800 + Math.random() * 600));
        }

        // TODO: Replace with actual Worker API call
        // const response = await fetch('/api/import/analyze', {
        //   method: 'POST',
        //   body: formData
        // });
        // const workerResults = await response.json();

        // Use the Worker results passed as props, or skip analysis if already provided
        if (propsWorkerResults) {
          setWorkerResults(propsWorkerResults);
          setAnalysisComplete(true);
          return;
        }

        setWorkerResults(workerResults);
        setAnalysisComplete(true);
      } catch (error) {
        console.error('Worker analysis failed:', error);
        // Handle error state
      }
    };

    analyzeWithWorker();
  }, [fileData, suggestedMappings]);

  if (!analysisComplete) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center space-x-4">
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">AI is Analyzing Your Data</h2>
            <p className="text-gray-600">
              Our AI is understanding your file structure and mapping fields automatically.
            </p>
          </div>
        </div>

        {/* AI Analysis Progress */}
        <Card>
          <CardContent className="p-8">
            <div className="text-center space-y-6">
              <div className="flex justify-center">
                <div className="relative">
                  <Brain className="w-16 h-16 text-blue-500 animate-pulse" />
                  <Sparkles className="w-6 h-6 text-yellow-500 absolute -top-1 -right-1 animate-bounce" />
                </div>
              </div>
              
              <div className="space-y-4">
                {aiAnalysisSteps.map((step, index) => (
                  <div key={step.id} className="flex items-center space-x-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      index < currentStep ? 'bg-green-100 text-green-600' :
                      index === currentStep ? 'bg-blue-100 text-blue-600' :
                      'bg-gray-100 text-gray-400'
                    }`}>
                      {index < currentStep ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : index === currentStep ? (
                        <div className="w-3 h-3 bg-current rounded-full animate-pulse" />
                      ) : (
                        <Clock className="w-4 h-4" />
                      )}
                    </div>
                    <div className="flex-1 text-left">
                      <div className={`font-medium ${index <= currentStep ? 'text-gray-900' : 'text-gray-400'}`}>
                        {step.title}
                      </div>
                      <div className={`text-sm ${index <= currentStep ? 'text-gray-600' : 'text-gray-400'}`}>
                        {step.description}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <Progress value={(currentStep / aiAnalysisSteps.length) * 100} className="w-full max-w-md mx-auto" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">✨ AI Analysis Complete!</h2>
          <p className="text-gray-600">
            We've automatically mapped your fields. Review and approve to start importing.
          </p>
        </div>
      </div>

      {/* Source Detection */}
      {workerResults?.sourceDetected && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-blue-100 rounded-full">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Source System Detected</h3>
                <p className="text-gray-600">
                  This looks like a <span className="font-medium text-blue-600">{workerResults.sourceDetected.system}</span> export 
                  ({Math.round(workerResults.sourceDetected.confidence * 100)}% confidence)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Smart Mappings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Wand2 className="w-5 h-5 text-purple-600" />
              <span>{isEditing ? 'Adjust Field Mappings' : 'Smart Field Mappings'}</span>
            </div>
            {!isEditing && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setIsEditing(true)}
              >
                Edit Mappings
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {fileData.headers.map((sourceColumn) => {
              const mapping = workerResults?.mappings?.[sourceColumn];
              return (
              <div key={sourceColumn} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-4 flex-1">
                  <div className="font-medium text-gray-900 min-w-0 flex-1">{sourceColumn}</div>
                  <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  
                  {isEditing ? (
                    <div className="min-w-0 flex-1">
                      <Select
                        value={editedMappings[sourceColumn] || mapping?.targetField || '-- Skip this column --'}
                        onValueChange={(value) => {
                          setEditedMappings(prev => ({
                            ...prev,
                            [sourceColumn]: value === '-- Skip this column --' ? '' : value
                          }));
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {getAvailableFields(importType).map((field) => (
                            <SelectItem key={field} value={field}>
                              {field}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div className={`font-medium min-w-0 flex-1 ${mapping ? 'text-blue-600' : 'text-gray-400'}`}>
                      {mapping?.targetField || '-- Not mapped --'}
                    </div>
                  )}
                </div>
                
                {!isEditing && mapping && (
                  <div className="flex items-center space-x-2 flex-shrink-0 ml-4">
                    <Badge variant="secondary" className="bg-green-50 text-green-700">
                      <Zap className="w-3 h-3 mr-1" />
                      {Math.round(mapping.confidence * 100)}%
                    </Badge>
                    <div className="text-xs text-gray-500 max-w-xs">
                      {mapping.reasoning}
                    </div>
                  </div>
                )}
                {!isEditing && !mapping && (
                  <div className="flex items-center space-x-2 flex-shrink-0 ml-4">
                    <Badge variant="outline" className="text-gray-500">
                      Manual mapping needed
                    </Badge>
                  </div>
                )}
              </div>
              );
            })}
          </div>
          
          {isEditing && (
            <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t">
              <Button 
                variant="outline"
                onClick={() => {
                  setIsEditing(false);
                  // Reset to original mappings for all headers
                  const originalMappings = fileData.headers.reduce((acc, header) => {
                    const mapping = workerResults?.mappings?.[header];
                    acc[header] = mapping?.targetField || '';
                    return acc;
                  }, {} as Record<string, string>);
                  setEditedMappings(originalMappings);
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={() => setIsEditing(false)}
              >
                Save Changes
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Data Quality Issues */}
      {workerResults?.dataQuality?.issues && workerResults.dataQuality.issues.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span>Data Quality Check</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="text-sm text-gray-600 mb-4">
                Found {workerResults.dataQuality.issues.length} minor issues that AI will fix automatically:
              </div>
              {workerResults.dataQuality.issues.map((issue, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg border border-green-200">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-green-900">{issue.description}</div>
                    <div className="text-xs text-green-700">AI will automatically fix this during import</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Eye className="w-5 h-5 text-gray-600" />
            <span>Preview</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600 mb-2">
                {workerResults?.dataQuality?.validRows || 0}
              </div>
              <div className="text-gray-600">
                rows ready to import out of {workerResults?.dataQuality?.totalRows || 0} total
              </div>
            </div>
            
            {workerResults?.previewData && workerResults.previewData.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900">Sample Transformed Data:</h4>
                {workerResults.previewData.slice(0, 2).map((row: any, index: number) => (
                  <div key={index} className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                    <div className="text-xs font-medium text-blue-800 mb-2">Row {index + 1}:</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="font-medium text-gray-700 mb-1">Original:</div>
                        <div className="space-y-1">
                          {Object.entries(row.original).map(([key, value]) => (
                            <div key={key} className="text-gray-600">
                              <span className="font-medium">{key}:</span> {value as string}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <div className="font-medium text-blue-700 mb-1">Mapped to Hivetivity:</div>
                        <div className="space-y-1">
                          {Object.entries(row.mapped).map(([key, value]) => (
                            <div key={key} className="text-blue-600">
                              <span className="font-medium">{key}:</span> {value as string}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          AI has mapped {Object.keys(workerResults?.mappings || {}).length} fields with high confidence
        </div>
        
        <div className="flex space-x-3">
          {!isEditing && (
            <Button 
              variant="outline"
              onClick={() => setIsEditing(true)}
            >
              Adjust Mappings
            </Button>
          )}
          <Button 
            onClick={() => onMappingComplete(
              // Use edited mappings if in editing mode, otherwise use original mappings
              isEditing ? editedMappings : Object.entries(workerResults?.mappings || {}).reduce((acc, [source, mapping]) => {
                acc[source] = mapping.targetField;
                return acc;
              }, {} as Record<string, string>)
            )}
            size="lg"
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            disabled={isEditing}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Start Import
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}